import { useRef } from "react";

export const DummyuseRecording = ({
    setAudioStream,
    setStream,
    setIsRecording,
    currentQuestion,
    setQuestionStatus,
    currentVivaQuestionId,
}) => {
    const mediaRecorderRef = useRef(null);
    const fullStreamRef = useRef(null);
    const recordedChunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user" },
                audio: true,
            });

            fullStreamRef.current = stream;
            setStream(stream);

            const audioOnly = new MediaStream(stream.getAudioTracks());
            setAudioStream(audioOnly);

            mediaRecorderRef.current = new MediaRecorder(audioOnly, {
                mimeType: "audio/webm;codecs=opus",
            });

            recordedChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) recordedChunksRef.current.push(e.data);
            };

            mediaRecorderRef.current.start(1000);
            setIsRecording(true);

            setQuestionStatus((prev = {}) => ({
                ...prev,
                [currentVivaQuestionId ?? currentQuestion]: "recording",
            }));

            console.log("[Recording] Started for question", currentVivaQuestionId || currentQuestion);
        } catch (err) {
            console.error("[Recording] getUserMedia failed:", err);
            alert("Camera / microphone permission denied?");
            setIsRecording(false);
        }
    };

    const forceStopTracks = () => {
        if (fullStreamRef.current) {
            const tracks = fullStreamRef.current.getTracks();
            console.log(`[Cleanup] Stopping ${tracks.length} tracks`);
            tracks.forEach((track) => {
                try {
                    track.stop();
                    track.enabled = false;
                } catch (e) {
                    console.warn("[Cleanup] Track stop failed:", e);
                }
            });
            fullStreamRef.current = null;
        } else {
            console.log("[Cleanup] No stream to stop");
        }
    };

    const stopRecording = async () => {
        if (!mediaRecorderRef.current) {
            console.warn("[Recording] No recorder – emergency cleanup");
            forceStopTracks();
            setIsRecording(false);
            setStream(null);
            setAudioStream(null);
            setQuestionStatus((prev) => ({
                ...prev,
                [currentVivaQuestionId ?? currentQuestion]: "answered",
            }));
            return { audioBlob: null };
        }

        return new Promise((resolve) => {
            let handled = false;

            const safetyTimeout = setTimeout(() => {
                if (handled) return;
                handled = true;
                console.error("[Recording] Timeout – forcing everything");
                forceStopTracks();
                setIsRecording(false);
                setStream(null);
                setAudioStream(null);
                setQuestionStatus((prev) => ({
                    ...prev,
                    [currentVivaQuestionId ?? currentQuestion]: "answered",
                }));
                resolve({ audioBlob: null });
            }, 4000);

            mediaRecorderRef.current.onstop = () => {
                if (handled) return;
                handled = true;
                clearTimeout(safetyTimeout);

                const blob = new Blob(recordedChunksRef.current, { type: "audio/webm" });
                console.log(`[onstop] Blob size: ${(blob.size / 1024).toFixed(1)} KB`);

                // Stop tracks RIGHT HERE – before resolve
                forceStopTracks();

                setStream(null);
                setAudioStream(null);
                setIsRecording(false);

                setQuestionStatus((prev) => ({
                    ...prev,
                    [currentVivaQuestionId ?? currentQuestion]: "answered",
                }));

                mediaRecorderRef.current = null;
                recordedChunksRef.current = [];

                resolve({ audioBlob: blob });
            };

            // ── CHANGED ORDER: STOP TRACKS FIRST ──
            console.log("[stopRecording] Stopping tracks before recorder");
            forceStopTracks();

            try {
                console.log("[stopRecording] Calling mediaRecorder.stop()");
                mediaRecorderRef.current.stop();
            } catch (err) {
                console.error("[stopRecording] .stop() failed:", err);
                handled = true;
                clearTimeout(safetyTimeout);
                forceStopTracks();
                setIsRecording(false);
                setStream(null);
                setAudioStream(null);
                setQuestionStatus((prev) => ({
                    ...prev,
                    [currentVivaQuestionId ?? currentQuestion]: "answered",
                }));
                resolve({ audioBlob: null });
            }
        });
    };

    return { startRecording, stopRecording };
};